import api from './api';
import { API_ROUTES } from '@/lib/constants';
import type { Signature } from '@/types/table';

interface CreateSignaturePayload {
  file: File;
  label: string;
  signatoryName: string;
  signatoryTitle?: string;
  isDefault?: boolean;
}

interface UpdateSignaturePayload {
  label?: string;
  signatoryName?: string;
  signatoryTitle?: string;
}

export const signatureService = {
  list: async (): Promise<{ success: boolean; data: Signature[] }> => {
    const response = await api.get(API_ROUTES.SIGNATURES);
    return response.data;
  },

  create: async (data: CreateSignaturePayload): Promise<{ success: boolean; data: Signature }> => {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('label', data.label);
    formData.append('signatoryName', data.signatoryName);
    if (data.signatoryTitle) formData.append('signatoryTitle', data.signatoryTitle);
    if (data.isDefault) formData.append('isDefault', 'true');
    // The shared axios instance sets `application/json` as the default Content-Type
    // (see services/api.ts). Override here so fastify-multipart parses the body.
    const response = await api.post(API_ROUTES.SIGNATURES, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  update: async (id: string, data: UpdateSignaturePayload): Promise<{ success: boolean; data: Signature }> => {
    const response = await api.patch(`${API_ROUTES.SIGNATURES}/${id}`, data);
    return response.data;
  },

  replaceFile: async (id: string, file: File): Promise<{ success: boolean; data: Signature }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`${API_ROUTES.SIGNATURES}/${id}/file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  setDefault: async (id: string): Promise<{ success: boolean; data: Signature }> => {
    const response = await api.post(`${API_ROUTES.SIGNATURES}/${id}/set-default`);
    return response.data;
  },

  remove: async (id: string): Promise<{ success: boolean; data: { id: string } }> => {
    const response = await api.delete(`${API_ROUTES.SIGNATURES}/${id}`);
    return response.data;
  },

  /** Public URL to <img>-render a signature. Cache-buster optional. */
  assetUrl: (id: string, cacheBuster?: string | number): string => {
    const apiBase = (import.meta.env.VITE_API_URL as string | undefined) || '/api/v1';
    const base = `${apiBase}${API_ROUTES.SIGNATURES}/${id}/file`;
    return cacheBuster ? `${base}?v=${cacheBuster}` : base;
  },
};
